import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    LinearProgress,
    TablePagination,
    MenuItem,
    Select,
    CircularProgress,
    Button,
    DialogActions,
    DialogContentText,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    useMediaQuery,
    List,
    ListItem,
    ListItemText,
    Divider,
    IconButton,
    FormControlLabel,
    FormGroup,
    Checkbox,
    Switch,
} from "@mui/material";
import {
    CheckCircle,
    Cancel,
    Edit,
    Block,
    Info,
    AddCircle,
    UploadFile as UploadFileIcon,
    Close as CloseIcon,
    Restore as RestoreIcon,
    PhotoCamera,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import baseURL from "../../../user/config";
import AddCampaignImagesDialog from "./AddCampaignImagesDialog";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { formatNumberWithCommas } from "../../../../Reusable/numberUtils";

const MS_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF5EE',
    text: '#323130',
    textSecondary: '#605D5C',
    background: '#FFFFFF',
    backgroundLight: '#F3F2F1',
    border: '#E1E1E1',
    hoverBackground: '#F0F0F0',
    white: '#FFFFFF',
    accent: '#107C10',
    red: '#D83B01',
};

// Define a key for localStorage
const DRAFT_STORAGE_KEY = 'newCampaignDraft';

// This component now receives `campaigns` and `refetchCampaigns` as props.
const CampaignsManagement = ({ campaigns, refetchCampaigns }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [statusFilter, setStatusFilter] = useState("all");

    const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDisableDialog, setOpenDisableDialog] = useState(false);
    const [openAddCampaignDialog, setOpenAddCampaignDialog] = useState(false);
    const [openAddImagesDialog, setOpenAddImagesDialog] = useState(false);

    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [disableReason, setDisableReason] = useState("");
    const [isDisabling, setIsDisabling] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const [editForm, setEditForm] = useState({
        name: "",
        description: "",
        goal: 0,
    });
    const [editImage, setEditImage] = useState(null);
    const [editImagePreview, setEditImagePreview] = useState(null);
    const editFileInputRef = useRef(null);

    const [newCampaignForm, setNewCampaignForm] = useState({
    name: "",
    goal: "",
    goalRaw: "",
    description: "",
    image: null,
    accepts_recurring: false,
    recurring_frequencies: [],
});


// NEW STATE to track if a draft exists
    const [draftExists, setDraftExists] = useState(false);

    // EFFECT 1: Check for an existing draft when the component mounts
    useEffect(() => {
        const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
        setDraftExists(!!savedDraft);
    }, []);

    // EFFECT 2: Auto-save form changes to localStorage
    useEffect(() => {
        // To prevent saving an empty form as a draft initially
        const isFormDirty = newCampaignForm.name || newCampaignForm.goal || newCampaignForm.description;

        if (isFormDirty) {
             // Exclude the 'image' File object, as it cannot be stored in JSON
            const { image, ...draftData } = newCampaignForm;
            localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
            setDraftExists(true);
        }
    }, [newCampaignForm]);

    const isSmallScreen = useMediaQuery("(max-width:600px)");

    // Handlers for backend interactions now call `refetchCampaigns` on success
    const handleEditCampaign = async () => {
        if (!selectedCampaign) return;
        setIsEditing(true);

        try {
            // Upload new image first if one was selected
            if (editImage) {
                const imgFormData = new FormData();
                imgFormData.append("image", editImage);
                const imgRes = await fetch(`${baseURL}/user/campaigns/${selectedCampaign.id}/image`, {
                    method: "POST",
                    credentials: "include",
                    body: imgFormData,
                });
                if (!imgRes.ok) {
                    const imgData = await imgRes.json();
                    throw new Error(imgData.error || "Failed to upload image");
                }
            }

            const response = await fetch(`${baseURL}/org/${selectedCampaign.id}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editForm),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update campaign.');
            }

            toast.success("Campaign updated successfully!");
            await refetchCampaigns();
            handleCloseEditDialog();
        } catch (error) {
            toast.error(error.message);
            console.error("Error updating campaign:", error);
        } finally {
            setIsEditing(false);
        }
    };

    const handleDisableCampaign = async () => {
        if (!selectedCampaign || !disableReason.trim()) {
            toast.error("Please provide a reason for disabling the campaign.");
            return;
        }
        setIsDisabling(true);

        try {
            const response = await fetch(`${baseURL}/org/${selectedCampaign.id}/close`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ reason: disableReason }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to disable campaign.');
            }

            toast.success("Campaign disabled successfully!");
            await refetchCampaigns(); // Trigger data refresh in parent
            handleCloseDisableDialog();
        } catch (error) {
            toast.error(error.message);
            console.error("Error disabling campaign:", error);
        } finally {
            setIsDisabling(false);
        }
    };

    // MODIFIED: handleAddCampaign to clear the draft on success
    const handleAddCampaign = async () => {
    const { name, goalRaw, description, image, accepts_recurring, recurring_frequencies } = newCampaignForm;

    const numericGoal = parseFloat(goalRaw);

    if (!name || !description || !image || isNaN(numericGoal) || numericGoal <= 0) {
        toast.error("Please fill in all required fields and ensure the campaign goal is a valid number greater than zero.");
        return; 
    }

    if (accepts_recurring && recurring_frequencies.length === 0) {
        toast.error("Please select at least one recurring frequency.");
        return;
    }

    setIsCreating(true);

    const formData = new FormData();
    formData.append("name", name);
    formData.append("goal", numericGoal);
    formData.append("description", description);
    formData.append("image", image);
    formData.append("accepts_recurring", accepts_recurring);
    formData.append("recurring_frequencies", JSON.stringify(recurring_frequencies));


        try {
            const response = await fetch(`${baseURL}/user/campaigns`, {
                method: "POST",
                credentials: "include",
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to create campaign.");
            }

            toast.success("New campaign created successfully!");
            await refetchCampaigns();
            
            // Clear the draft from storage on successful submission
            localStorage.removeItem(DRAFT_STORAGE_KEY);
            setDraftExists(false);

            handleCloseAddCampaignDialog();
        } catch (error) {
            toast.error(error.message);
            console.error("Error creating campaign:", error);
        } finally {
            setIsCreating(false);
        }
    };

const handleOpenAddCampaignDialog = () => {
        setNewCampaignForm({ name: "", goal: "", goalRaw: "", description: "", image: null, accepts_recurring: false, recurring_frequencies: [] });
        setOpenAddCampaignDialog(true);
    };

    const handleCloseAddCampaignDialog = () => {
        setOpenAddCampaignDialog(false);
        setNewCampaignForm({ name: "", goal: "", goalRaw: "", description: "", image: null, accepts_recurring: false, recurring_frequencies: [] });
    };

    // NEW HANDLER: To load the draft and open the dialog
    const handleOpenDraftDialog = () => {
        const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (savedDraft) {
            const draftData = JSON.parse(savedDraft);
            setNewCampaignForm(prev => ({
                ...prev, // Keeps image: null
                ...draftData,
            }));
            toast.info("Draft loaded. Please re-select an image.");
            setOpenAddCampaignDialog(true);
        } else {
            toast.warn("No draft found to load.");
        }
    };
    
    // NEW HANDLER: To discard the saved draft
    const handleDiscardDraft = () => {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        setDraftExists(false);
        toast.info("Draft has been discarded.");
    };

    // All other dialog and state management functions remain the same
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleFilterChange = (event) => setStatusFilter(event.target.value);

    const handleOpenDetailsDialog = (campaign) => {
        setSelectedCampaign(campaign);
        setOpenDetailsDialog(true);
    };
    const handleCloseDetailsDialog = () => {
        setOpenDetailsDialog(false);
        setSelectedCampaign(null);
    };

    const handleOpenEditDialog = () => {
        setEditForm({ name: selectedCampaign.name, description: selectedCampaign.description, goal: selectedCampaign.goal });
        setEditImage(null);
        setEditImagePreview(null);
        setOpenDetailsDialog(false);
        setOpenEditDialog(true);
    };
    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setEditImage(null);
        setEditImagePreview(null);
        setSelectedCampaign(null);
    };
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm((prev) => ({ ...prev, [name]: value }));
    };
    const handleEditImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowed.includes(file.type)) {
            toast.error('Only JPG, JPEG, and PNG images are allowed');
            return;
        }
        setEditImage(file);
        setEditImagePreview(URL.createObjectURL(file));
    };

    const handleOpenDisableDialog = (campaign) => {
        setSelectedCampaign(campaign);
        setDisableReason("");
        setOpenDisableDialog(true);
    };
    const handleCloseDisableDialog = () => {
        setOpenDisableDialog(false);
        setSelectedCampaign(null);
    };

  

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) { // 2MB size limit
                toast.error(`Image "${file.name}" exceeds the 2MB size limit.`);
                return;
            }
            setNewCampaignForm((prev) => ({ ...prev, image: file }));
        }
    };

    const handleRemoveImage = () => {
        setNewCampaignForm((prev) => ({ ...prev, image: null }));
    };

    const handleOpenAddImagesDialog = (campaign) => {
        setSelectedCampaign(campaign);
        setOpenAddImagesDialog(true);
    };

    const handleCloseAddImagesDialog = () => {
        setOpenAddImagesDialog(false);
        setSelectedCampaign(null);
        refetchCampaigns();
    };

    const filteredCampaigns = statusFilter === "all"
        ? campaigns
        : campaigns.filter((campaign) => campaign.status === statusFilter);

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.05)",
                p: 3,
                mt: 3,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: "#1a1a2e",
                        fontWeight: 800,
                        fontSize: isSmallScreen ? "1.05rem" : "1.2rem",
                        letterSpacing: "-0.3px",
                    }}
                >
                    Campaigns Management
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'right', gap: 1}}>
                    {draftExists && (
                        <>
                            <Button
                                variant="outlined"
                                onClick={handleOpenDraftDialog}
                                sx={{ textTransform: 'none' }}
                                startIcon={<RestoreIcon />}
                            >
                                Load Draft
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDiscardDraft}
                                sx={{ mr: 1, textTransform: 'none' }}
                            >
                                Discard Draft
                            </Button>
                        </>
                    )}
                </Box>
                
                <Button
                    variant="contained"
                    onClick={handleOpenAddCampaignDialog}
                    sx={{
                        backgroundColor: MS_COLORS.primary,
                        color: MS_COLORS.white,
                        textTransform: 'none',
                        fontSize: "0.85rem",
                        py: 1,
                        px: 2,
                        borderRadius: 1,
                        boxShadow: 0,
                        '&:hover': {
                            backgroundColor: MS_COLORS.primary,
                            opacity: 0.9,
                            boxShadow: 0,
                        },
                        mr: 1,
                    }}
                    startIcon={<AddCircle />}
                >
                    Add Campaign
                </Button>
            </Box>

            <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
                <Select
                    value={statusFilter}
                    onChange={handleFilterChange}
                    size="small"
                    sx={{
                        minWidth: 150,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: MS_COLORS.border },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: MS_COLORS.primary },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: MS_COLORS.primary },
                        "& .MuiSelect-select": { color: MS_COLORS.text, fontSize: '0.875rem' },
                    }}
                >
                    <MenuItem value="all">All Campaigns</MenuItem>
                    <MenuItem value="open">Open Campaigns</MenuItem>
                    <MenuItem value="closed">Closed Campaigns</MenuItem>
                    <MenuItem value="disabled">Disabled Campaigns</MenuItem>
                </Select>
            </Box>

            {/* No need for a separate isLoading state here, as the parent handles it */}
            {filteredCampaigns.length === 0 ? (
                <Box
                    sx={{
                        textAlign: "center",
                        color: MS_COLORS.textSecondary,
                        mt: 4,
                        mb: 4,
                        fontSize: "0.9rem",
                        p: 3,
                        border: `1px dashed ${MS_COLORS.border}`,
                        borderRadius: 1,
                        bgcolor: MS_COLORS.backgroundLight,
                    }}
                >
                    <Typography variant="body1">
                        No campaigns available matching the selected filter.
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer
                        component={Paper}
                        sx={{
                            borderRadius: 1,
                            boxShadow: 0,
                            overflowX: "auto",
                            border: `1px solid ${MS_COLORS.border}`,
                        }}
                    >
                        <Table sx={{ minWidth: 750 }}>
                            <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Campaign Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Goal (UGX)</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Amount Raised (UGX)</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Date Posted</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Progress</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCampaigns
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((campaign) => {
                                        const progress = (campaign.raised / campaign.goal) * 100;
                                        return (
                                            <TableRow
                                                key={campaign.id}
                                                sx={{
                                                    "&:last-child td, &:last-child th": { border: 0 },
                                                    "&:hover": { backgroundColor: MS_COLORS.hoverBackground },
                                                }}
                                            >
                                                <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{campaign.name}</TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>UGX {campaign.goal.toLocaleString()}</TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>UGX {campaign.raised.toLocaleString()}</TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{new Date(campaign.created_at).toLocaleDateString()}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: isSmallScreen ? 100 : 150 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={progress > 100 ? 100 : progress}
                                                            sx={{
                                                                flexGrow: 1,
                                                                height: 8,
                                                                borderRadius: 5,
                                                                bgcolor: MS_COLORS.border,
                                                                "& .MuiLinearProgress-bar": { bgcolor: MS_COLORS.accent },
                                                            }}
                                                        />
                                                        <Typography sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{progress.toFixed(2)}%</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        {campaign.status === "open" && <CheckCircle sx={{ color: MS_COLORS.accent, fontSize: "small" }} />}
                                                        {campaign.status === "closed" && <Cancel sx={{ color: MS_COLORS.red, fontSize: "small" }} />}
                                                        {campaign.status === "disabled" && <Block sx={{ color: MS_COLORS.textSecondary, fontSize: "small" }} />}
                                                        <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold", color: MS_COLORS.text }}>
                                                            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleOpenDetailsDialog(campaign)}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontSize: "0.75rem",
                                                            borderRadius: 1,
                                                            borderColor: MS_COLORS.primary,
                                                            boxShadow: 0,
                                                            color: MS_COLORS.primary,
                                                            mr: 1,
                                                            "&:hover": {
                                                                backgroundColor: MS_COLORS.primaryLight,
                                                                borderColor: MS_COLORS.primary,
                                                            }
                                                        }}
                                                        startIcon={<Info />}
                                                    >
                                                        Details
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleOpenAddImagesDialog(campaign)}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontSize: "0.75rem",
                                                            borderRadius: 1,
                                                            borderColor: MS_COLORS.primary,
                                                            boxShadow: 0,
                                                            color: MS_COLORS.primary,
                                                            mr: 1,
                                                            "&:hover": {
                                                                backgroundColor: MS_COLORS.primaryLight,
                                                                borderColor: MS_COLORS.primary,
                                                            }
                                                        }}
                                                        startIcon={<UploadFileIcon />}
                                                    >
                                                        Manage Images
                                                    </Button>
                                                    {campaign.status === "open" && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={() => handleOpenDisableDialog(campaign)}
                                                            sx={{
                                                                textTransform: "none",
                                                                fontSize: "0.75rem",
                                                                borderRadius: 1,
                                                                borderColor: MS_COLORS.red,
                                                                boxShadow: 0,
                                                                color: MS_COLORS.red,
                                                                "&:hover": {
                                                                    backgroundColor: MS_COLORS.red + '10',
                                                                    borderColor: MS_COLORS.red,
                                                                }
                                                            }}
                                                            startIcon={<Block />}
                                                        >
                                                            Close
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredCampaigns.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.8rem", color: MS_COLORS.textSecondary } }}
                    />
                </>
            )}

            {/* Dialogs remain the same, as they manage their own local state and call `refetchCampaigns` */}
            {/* Campaign Details Dialog */}
            <Dialog
                open={openDetailsDialog}
                onClose={handleCloseDetailsDialog}
                PaperProps={{ sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}
            >
                {selectedCampaign && (
                    <>
                        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
                            Campaign Details: {selectedCampaign.name}
                        </DialogTitle>
                        <DialogContent>
                            <Box mb={2}>
                                <img
                                    src={selectedCampaign.image_url}
                                    alt={selectedCampaign.name}
                                    style={{ width: "100%", height: "auto", borderRadius: "4px" }}
                                />
                            </Box>
                            <Box mb={2}>
                                <Box mb={2}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        Description:
                                    </Typography>
                                    <Box
                                        sx={{
                                            color: MS_COLORS.textSecondary,
                                            '& p:last-child': { mb: 0 },
                                            '& ul, & ol': { pl: 2 },
                                        }}
                                    >
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            rehypePlugins={[rehypeRaw]}
                                        >
                                            {selectedCampaign.description}
                                        </ReactMarkdown>
                                    </Box>
                                </Box>
                            </Box>
                            <Divider />
                            <Box my={2}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    Goal:
                                </Typography>
                                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary }}>
                                    UGX {selectedCampaign.goal.toLocaleString()}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box my={2}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    Amount Raised:
                                </Typography>
                                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary }}>
                                    UGX {selectedCampaign.raised.toLocaleString()}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box mt={2}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0, fontSize: '1.3rem' }}>
                                    Donations
                                </Typography>
                                {selectedCampaign.donors?.length > 0 ? (
                                    <List dense>
                                        {selectedCampaign.donors.map((donor) => (
                                            <ListItem key={donor.id} sx={{ py: 0.5, px: 0 }}>
                                                <ListItemText
                                                    primary={donor.user_name}
                                                    secondary={`Donated: UGX ${donor.amount.toLocaleString()} | Donated on: ${new Date(donor.created_at).toLocaleDateString()}`}
                                                    primaryTypographyProps={{ fontSize: '0.8rem' }}
                                                    secondaryTypographyProps={{ fontSize: '0.6rem' }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary }}>
                                        No donors yet.
                                    </Typography>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ pb: 1, px: 3, pt: 2 }}>
                            <Button onClick={handleCloseDetailsDialog} variant="outlined" sx={{ borderRadius: "4px", textTransform: "none" }}>
                                Close
                            </Button>
                            <Button
                                onClick={handleOpenEditDialog}
                                variant="contained"
                                sx={{
                                    backgroundColor: MS_COLORS.primary, textTransform: "none",
                                    boxShadow: 0,
                                    '&:hover': {
                                        backgroundColor: MS_COLORS.primary,
                                        opacity: 0.9,
                                        boxShadow: 0,
                                    },
                                }}
                                startIcon={<Edit />}
                            >
                                Edit Campaign
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Edit Campaign Dialog */}
            <Dialog
                open={openEditDialog}
                onClose={handleCloseEditDialog}
                PaperProps={{ sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}
            >
                <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
                    Edit Campaign
                </DialogTitle>
                <DialogContent>
                    {/* Campaign Photo */}
                    <Box sx={{ mb: 2, mt: 1 }}>
                        <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mb: 1 }}>Campaign Photo</Typography>
                        <Box sx={{
                            position: 'relative', borderRadius: 2, overflow: 'hidden',
                            border: `1px solid ${MS_COLORS.border}`, height: 160, display: 'flex',
                            justifyContent: 'center', alignItems: 'center', backgroundColor: MS_COLORS.backgroundLight
                        }}>
                            {(editImagePreview || (selectedCampaign && selectedCampaign.image_url)) ? (
                                <img
                                    src={editImagePreview || selectedCampaign.image_url}
                                    alt="Campaign"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">No Image</Typography>
                            )}
                            <IconButton
                                onClick={() => editFileInputRef.current?.click()}
                                sx={{
                                    position: 'absolute', bottom: 8, right: 8,
                                    backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                                }}
                                size="small"
                            >
                                <PhotoCamera fontSize="small" />
                            </IconButton>
                        </Box>
                        <input
                            ref={editFileInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            hidden
                            onChange={handleEditImageSelect}
                        />
                        {editImage && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                New image: {editImage.name}
                            </Typography>
                        )}
                    </Box>

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Campaign Name"
                        name="name"
                        value={editForm.name}
                        onChange={handleEditChange}
                        sx={{ mt: 2 }}
                    />
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mb: 1 }}>
                            Edit the description of your campaign on the left. This will help potential donors understand what your campaign is about. Ensure the description is clear as it will be visible on the website.
                        </Typography>
                        <div data-color-mode="light">
                            <MDEditor
                                value={editForm.description}
                                onChange={(value) => setEditForm((prev) => ({ ...prev, description: value }))}
                                height={200}
                            />
                        </div>
                    </Box>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Campaign Goal (UGX)"
                        name="goal"
                        type="number"
                        value={editForm.goal}
                        onChange={handleEditChange}
                    />
                </DialogContent>
                <DialogActions sx={{ pb: 1, px: 3 }}>
                    <Button onClick={handleCloseEditDialog} variant="outlined" sx={{ borderRadius: "4px", textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEditCampaign}
                        variant="contained"
                        disabled={isEditing}
                        sx={{
                            backgroundColor: MS_COLORS.primary, textTransform: "none",
                            boxShadow: 0,
                            '&:hover': {
                                backgroundColor: MS_COLORS.primary,
                                opacity: 0.9,
                                boxShadow: 0,
                            },
                        }}
                    >
                        {isEditing ? (
                            <>
                                <CircularProgress size={18} sx={{ color: MS_COLORS.white, mr: 1 }} />
                                Saving...
                            </>
                        ) : (
                            "Save Changes"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Disable Campaign Dialog */}
            <Dialog
                open={openDisableDialog}
                onClose={handleCloseDisableDialog}
                PaperProps={{ sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}
            >
                <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
                    Confirm Disable Campaign
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: "0.9rem", color: MS_COLORS.textSecondary, mb: 2 }}>
                        Are you sure you want to disable this campaign? Please provide a reason.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason for Disabling"
                        variant="outlined"
                        value={disableReason}
                        onChange={(e) => setDisableReason(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ pb: 1, px: 3 }}>
                    <Button onClick={handleCloseDisableDialog} variant="outlined" sx={{ borderRadius: "4px", textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDisableCampaign}
                        variant="contained"
                        disabled={isDisabling || !disableReason.trim()}
                        sx={{ backgroundColor: MS_COLORS.red, color: MS_COLORS.white, textTransform: "none", boxShadow: 'none' }}
                    >
                        {isDisabling ? (
                            <>
                                <CircularProgress size={18} sx={{ color: MS_COLORS.white, mr: 1 }} />
                                Closing...
                            </>
                        ) : (
                            "Confirm Close"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Campaign Dialog */}
            <Dialog
                open={openAddCampaignDialog}
                onClose={handleCloseAddCampaignDialog}
                PaperProps={{ sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}
            >
                <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
                    Add New Campaign
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Campaign Name"
                        name="name"
                        value={newCampaignForm.name}
                        onChange={(e) => setNewCampaignForm({ ...newCampaignForm, name: e.target.value })}
                        sx={{ mt: 2 }}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Campaign Goal (UGX)"
                        name="goal"
                        type="text"
                        value={newCampaignForm.goal}
                        onChange={(e) => {
                            const rawValue = e.target.value.replace(/,/g, "").replace(/\D/g, "");
                            setNewCampaignForm({
                                ...newCampaignForm,
                                goal: formatNumberWithCommas(rawValue),
                                goalRaw: rawValue,
                            });
                        }}
                    />
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mb: 1 }}>
                            Write a brief description of your campaign on the left. This will help potential donors understand what your campaign is about. Ensure the description is clear as it will be visible on the website.
                        </Typography>
                        <div data-color-mode="light">
                            <MDEditor
                                value={newCampaignForm.description}
                                onChange={(value) => setNewCampaignForm({ ...newCampaignForm, description: value })}
                                height={200}
                            />
                        </div>
                    </Box>
                    <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="campaign-image-upload"
                        type="file"
                        onChange={handleImageUpload}
                    />
                    <label htmlFor="campaign-image-upload" style={{ display: 'block', marginTop: '16px' }}>
                        <Button
                            variant="outlined"
                            component="span"
                            fullWidth
                            sx={{
                                backgroundColor: MS_COLORS.primary,
                                textTransform: "none",
                                color: 'white',
                                py: 1.2,
                                "&:hover": { backgroundColor: MS_COLORS.primary, opacity: 1 },
                            }}
                            startIcon={<UploadFileIcon />}
                        >
                            Upload Primary Campaign Image
                        </Button>
                    </label>
                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1, color: newCampaignForm.image ? MS_COLORS.accent : MS_COLORS.red }}
                    >
                        {newCampaignForm.image ? `Image Selected: ${newCampaignForm.image.name}` : "No image selected. Required for new campaign."}
                    </Typography>
                    {newCampaignForm.image && (
                        <IconButton
                            onClick={handleRemoveImage}
                            sx={{ color: MS_COLORS.red, ml: 1, p: 0 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}

                    {/* Recurring donations opt-in */}
                    <Box sx={{ mt: 3 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={newCampaignForm.accepts_recurring}
                                    onChange={(e) => setNewCampaignForm({
                                        ...newCampaignForm,
                                        accepts_recurring: e.target.checked,
                                        recurring_frequencies: e.target.checked ? newCampaignForm.recurring_frequencies : [],
                                    })}
                                    color="primary"
                                />
                            }
                            label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Accept recurring donations</Typography>}
                        />
                        {newCampaignForm.accepts_recurring && (
                            <Box sx={{ mt: 1, pl: 1 }}>
                                <Typography variant="caption" sx={{ color: MS_COLORS.textSecondary, display: 'block', mb: 0.5 }}>
                                    Select available frequencies (at least one required):
                                </Typography>
                                <FormGroup row>
                                    {['daily', 'weekly', 'monthly', 'annually'].map((freq) => (
                                        <FormControlLabel
                                            key={freq}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={newCampaignForm.recurring_frequencies.includes(freq)}
                                                    onChange={(e) => {
                                                        const updated = e.target.checked
                                                            ? [...newCampaignForm.recurring_frequencies, freq]
                                                            : newCampaignForm.recurring_frequencies.filter(f => f !== freq);
                                                        setNewCampaignForm({ ...newCampaignForm, recurring_frequencies: updated });
                                                    }}
                                                    sx={{ color: MS_COLORS.primary, '&.Mui-checked': { color: MS_COLORS.primary } }}
                                                />
                                            }
                                            label={<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{freq}</Typography>}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ pb: 1, px: 3 }}>
                    <Button onClick={handleCloseAddCampaignDialog} variant="outlined" sx={{ borderRadius: "4px", textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddCampaign}
                        variant="contained"
                        disabled={isCreating}
                        sx={{
                            backgroundColor: MS_COLORS.primary,
                            color: MS_COLORS.white,
                            textTransform: "none",
                            boxShadow: 0,
                            '&:hover': {
                                backgroundColor: MS_COLORS.primary,
                                opacity: 0.9,
                                boxShadow: 0,
                            },
                        }}
                    >
                        {isCreating ? (
                            <>
                                <CircularProgress size={18} sx={{ color: MS_COLORS.white, mr: 1 }} />
                                Creating...
                            </>
                        ) : (
                            "Add Campaign"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <AddCampaignImagesDialog
                open={openAddImagesDialog}
                onClose={handleCloseAddImagesDialog}
                campaignId={selectedCampaign?.id}
                initialImages={selectedCampaign?.images || []}
            />
        </Box>
    );
};

export default CampaignsManagement;